/**
 * Change-Request Dialog + History Dialog for MyProfile.
 * Supports multi-field requests via a single dialog.
 */
import React, { useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Send } from 'lucide-react';
import { EDITABLE_FIELDS, StatusBadge, formatDateTime } from './profileHelpers';

export const ChangeRequestDialog = ({
  open, onOpenChange,
  selectedCategory, setSelectedCategory,
  fieldValues, setFieldValues,       // { field_name: 'new value' } across categories
  reason, setReason,
  employee,
  submitting, onSubmit,
}) => {
  const categoryFields = selectedCategory
    ? Object.entries(EDITABLE_FIELDS[selectedCategory]?.fields || {})
    : [];

  const selectedCount = useMemo(
    () => Object.values(fieldValues || {}).filter((v) => v && v.value != null && String(v.value).trim() !== '').length,
    [fieldValues]
  );

  const toggleField = (category, fieldName) => {
    const key = `${category}:${fieldName}`;
    setFieldValues((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = { category, field_name: fieldName, value: '' };
      }
      return next;
    });
  };

  const updateValue = (category, fieldName, value) => {
    const key = `${category}:${fieldName}`;
    setFieldValues((prev) => ({
      ...prev,
      [key]: { category, field_name: fieldName, value },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Änderung beantragen</DialogTitle>
          <DialogDescription>
            Wählen Sie die Kategorie und mehrere Felder aus, die Sie ändern möchten.
            Pro Feld wird ein Antrag erstellt; die Personalabteilung kann jede Änderung einzeln genehmigen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Kategorie</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="category-select"><SelectValue placeholder="Kategorie wählen" /></SelectTrigger>
              <SelectContent>
                {Object.entries(EDITABLE_FIELDS).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedCategory && (
            <div className="space-y-3 rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">
                Mehrere Felder auswählen und jeweils den neuen Wert eingeben.
              </p>
              {categoryFields.map(([fieldName, config]) => {
                const key = `${selectedCategory}:${fieldName}`;
                const entry = fieldValues[key];
                const isChecked = Boolean(entry);
                const currentValue = employee?.[fieldName];
                return (
                  <div key={fieldName} className="flex items-start gap-3">
                    <Checkbox
                      id={`chk-${key}`}
                      checked={isChecked}
                      onCheckedChange={() => toggleField(selectedCategory, fieldName)}
                      data-testid={`chk-${fieldName}`}
                      className="mt-3"
                    />
                    <div className="flex-1 min-w-0">
                      <Label htmlFor={`chk-${key}`} className="text-sm font-medium">
                        {config.label}
                      </Label>
                      <p className="text-[11px] text-muted-foreground truncate">
                        Aktuell: {currentValue || '(leer)'}
                      </p>
                      {isChecked && (
                        <Input
                          type={config.type || 'text'}
                          value={entry.value}
                          onChange={(e) => updateValue(selectedCategory, fieldName, e.target.value)}
                          placeholder="Neuen Wert eingeben"
                          className="mt-1"
                          data-testid={`input-${fieldName}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {selectedCategory && (
            <div className="space-y-2">
              <Label>Begründung (optional)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Gilt für alle ausgewählten Felder..."
                rows={2}
                data-testid="reason-input"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex items-center w-full justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {selectedCount} Feld{selectedCount !== 1 ? 'er' : ''} ausgefüllt
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
              <Button
                onClick={onSubmit}
                disabled={selectedCount === 0 || submitting}
                data-testid="submit-change-request"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                {selectedCount > 1 ? `${selectedCount} Anträge einreichen` : 'Antrag einreichen'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const HistoryDialog = ({ open, onOpenChange, selectedRequest }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Antragsverlauf</DialogTitle>
      </DialogHeader>
      {selectedRequest && (
        <div className="space-y-4">
          <div className="p-3 bg-secondary/30 rounded-lg">
            <p className="font-medium">{selectedRequest.field_label}</p>
            <p className="text-sm text-muted-foreground">
              {selectedRequest.current_value || '(leer)'} → {selectedRequest.new_value}
            </p>
          </div>
          <div className="space-y-3">
            {selectedRequest.status_history?.map((entry, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg">
                <StatusBadge status={entry.status} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{entry.changed_by_name}</p>
                  <p className="text-xs text-muted-foreground">{formatDateTime(entry.timestamp)}</p>
                  {entry.rejection_reason && (
                    <p className="text-sm text-red-500 mt-1">Grund: {entry.rejection_reason}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
